import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { IoEllipsisVertical } from 'react-icons/io5';
import usePdfProcessor from '../hooks/usePdfProcessor';
import truncateString from '../helpers/truncateString';
import { deleteByKey } from '../helpers/indexedDb';
import '../style/UploadedFilesList.css';

const formatDate = (timestamp) => {
  if (!timestamp) return '—';
  const d = new Date(timestamp);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatFromFileName = (fileName) => {
  if (!fileName) return 'PDF';
  const ext = fileName.split('.').pop()?.toUpperCase();
  return ext || 'PDF';
};

const INITIAL_COUNT = 5;

const UploadedFilesList = () => {
  const { t } = useTranslation();
  const { getAllPdfFiles, deletePdfFile } = usePdfProcessor();
  const [files, setFiles] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const refreshFiles = useCallback(() => {
    getAllPdfFiles().then(setFiles);
  }, [getAllPdfFiles]);

  useEffect(() => {
    let cancelled = false;
    getAllPdfFiles().then((list) => {
      if (!cancelled) setFiles(list);
    });
    return () => { cancelled = true; };
  }, [getAllPdfFiles]);

  useEffect(() => {
    if (openMenuId === null) {
      setDropdownPosition(null);
      return;
    }
    const updatePosition = () => {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const minWidth = 120;
      const dropdownLeft = Math.max(8, Math.min(rect.right - minWidth, window.innerWidth - minWidth - 8));
      const dropdownTop = rect.bottom + 4;
      setDropdownPosition({
        top: dropdownTop,
        left: dropdownLeft,
      });
    };
    const raf = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(raf);
  }, [openMenuId]);

  useEffect(() => {
    if (openMenuId === null) return;
    const handleOutside = (e) => {
      const inside = menuRef.current?.contains(e.target) || dropdownRef.current?.contains(e.target);
      if (!inside) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [openMenuId]);

  const handleDelete = useCallback(async (id) => {
    setOpenMenuId(null);
    try {
      await deletePdfFile(id);
      await deleteByKey(id);
      refreshFiles();
    } catch (err) {
      console.error('Delete file failed:', err);
    }
  }, [deletePdfFile, refreshFiles]);

  const toggleMenu = useCallback((id) => {
    setOpenMenuId((prev) => (prev === id ? null : id));
  }, []);

  if (files.length === 0) return null;

  const visibleFiles = showAll ? files : files.slice(0, INITIAL_COUNT);
  const hasMore = files.length > INITIAL_COUNT;

  return (
    <div className="uploaded-files-section content-section">
      <div className="uploaded-files-list">
        {visibleFiles.map(({ id, fileName, timestamp }) => (
          <div key={id} className={`uploaded-file-card${openMenuId === id ? ' menu-open' : ''}`}>
            <Link to={`/file/${id}`} className="uploaded-file-card-link" title={fileName}>
              <div className="uploaded-file-card-main">
                <span className="uploaded-file-card-format">{formatFromFileName(fileName)}</span>
                <span className="uploaded-file-card-name">{truncateString(fileName, 48)}</span>
              </div>
              <span className="uploaded-file-card-date">{formatDate(timestamp)}</span>
            </Link>
            <div className="uploaded-file-card-actions" ref={openMenuId === id ? menuRef : null}>
              <button
                ref={openMenuId === id ? buttonRef : null}
                type="button"
                className="uploaded-file-menu-btn"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleMenu(id); }}
                title={t('common.actions')}
                aria-label={t('common.actions')}
              >
                <IoEllipsisVertical />
              </button>
            </div>
          </div>
        ))}
      </div>
      {hasMore && !showAll && (
        <div className="uploaded-files-show-more-wrap">
          <button
            type="button"
            className="btn-show-more"
            onClick={() => setShowAll(true)}
          >
            {t('common.showMore')}
          </button>
        </div>
      )}
      {openMenuId && dropdownPosition && createPortal(
          <div
            ref={dropdownRef}
            className="uploaded-file-dropdown"
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              zIndex: 10000,
            }}
          >
            <button
              type="button"
              className="uploaded-file-dropdown-item uploaded-file-dropdown-delete"
              onClick={(e) => { e.preventDefault(); handleDelete(openMenuId); }}
            >
              {t('common.delete')}
            </button>
          </div>,
        document.body
      )}
    </div>
  );
};

export default UploadedFilesList;
